import { useState, useEffect } from 'react';
import { collection, getDocs, query, where, documentId } from 'firebase/firestore';
import { db } from '../firebase/config';

const CHUNK_SIZE = 10; // Firestore supports up to 10 for 'in' queries

const useCourseAuthors = (courses = []) => {
  const [authors, setAuthors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchAuthors = async () => {
      if (!courses.length) {
        setAuthors([]);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);

        // Get unique author IDs from courses (prefer userId/author_id fields that match user doc ids)
        const potentialIds = courses
          .map((course) => {
            if (course.userId) return String(course.userId);
            if (course.author_id) return String(course.author_id);
            if (course.author && typeof course.author === 'object' && course.author.id)
              return String(course.author.id);
            // If course.author is string, it may be uid or email; prefer only uid-like strings
            if (course.author && typeof course.author === 'string') return String(course.author);
            return null;
          })
          .filter(Boolean);

        const uniqueIds = Array.from(new Set(potentialIds));
        if (uniqueIds.length === 0) {
          setAuthors([]);
          setLoading(false);
          return;
        }

        const usersRef = collection(db, 'users');
        const fetched = [];

        // Chunk by 10 for 'in' queries
        for (let i = 0; i < uniqueIds.length; i += CHUNK_SIZE) {
          const chunk = uniqueIds.slice(i, i + CHUNK_SIZE);
          const q = query(usersRef, where(documentId(), 'in', chunk));
          const snap = await getDocs(q);
          snap.forEach((docSnap) => {
            fetched.push({ id: docSnap.id, ...docSnap.data() });
          });
        }

        setAuthors(fetched);
        setError(null);
      } catch (err) {
        console.error('Error fetching course authors:', err);
        setError('Failed to fetch course authors');
      } finally {
        setLoading(false);
      }
    };

    fetchAuthors();
  }, [courses]);

  // Helper functions
  const getAuthor = (userId) => {
    return authors.find((author) => author.id === userId);
  };

  const getAuthorName = (userId) => {
    const author = getAuthor(userId);
    return author ? author.name || author.email || 'Unknown Author' : 'Unknown Author';
  };

  // Get author name directly from course if author is an object
  const getAuthorNameFromCourse = (course) => {
    if (course.author && typeof course.author === 'object') {
      return course.author.name || course.author.email || 'Unknown Author';
    }
    return getAuthorName(getAuthorId(course));
  };

  // Get author ID from course (check both userId and author fields)
  const getAuthorId = (course) => {
    if (course.userId) return String(course.userId);
    if (course.author && typeof course.author === 'object' && course.author.id) {
      return String(course.author.id);
    }
    if (course.author && typeof course.author === 'string') {
      return String(course.author);
    }
    if (course.author_id) return String(course.author_id);
    return null;
  };

  const getAuthorStats = () => {
    const authorPostCounts = {};
    const authorRevenue = {};

    courses.forEach((course) => {
      const authorId = getAuthorId(course);
      if (authorId) {
        authorPostCounts[authorId] = (authorPostCounts[authorId] || 0) + 1;

        if (course.price_status === 'paid' && course.pricee) {
          const price = parseFloat(course.pricee) || 0;
          const studentCount = course.enrolled_students?.length || course.students || 0;
          authorRevenue[authorId] = (authorRevenue[authorId] || 0) + price * studentCount;
        }
      }
    });

    return {
      authorPostCounts,
      authorRevenue,
      totalAuthors: Object.keys(authorPostCounts).length,
    };
  };

  return {
    authors,
    loading,
    error,
    getAuthor,
    getAuthorName,
    getAuthorNameFromCourse,
    getAuthorId,
    getAuthorStats,
  };
};

export default useCourseAuthors;
